import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore, bindActionCreators } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCartContainer from './Containers/MiniCart.container';
import FavouriteIcon from './Components/FavouriteIcon';
import QuickSearchContainer from './Containers/QuickSearch.container';
import NavControllerContainer from './Containers/NavController.Container';
import NavigationContainer from './Containers/Navigation.container';
import MainNavigation from './Components/MainNavigation';
import MobileMenu from './Components/MobileMenu';
import FacetedSearchContainer from './Containers/FacetedSearch.container';
import FacetedSearchCompactContainer from './Containers/FacetedSearchCompact.container';
import NotificationContainer from './Containers/Notification.container';
import DynamicComponent from './Components/DynamicComponent';
import { ToastContainer } from 'react-toastify';
import './Containers/VanillaContainer';
import { add as addToWishList, addToCart } from './Actions/Wishlist.action';
import { translate } from './Services/translation';
import "../Styles/tailwind.css";
import "../Styles/site.scss";

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
// use the parent page's store if possible, so the iframe will share the same store with the parent context.
// this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
// when reorder action happens.
const store = window.__litium.store = window.__litium.store || (window.parent.__litium && window.parent.__litium.store) 
    || createStore(app, preloadState, composeWithDevTools(applyMiddleware(thunk, historyMiddleware)));
window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
    reduxActions: bindActionCreators({ addToCart, addToWishList} , store.dispatch),
};

const setupMobileMenu = () => {
    const el = document.getElementById('mobileMenu');
    if (!el) return;

    let data = {};
    try {
        data = JSON.parse(el.dataset.navigation || '{}');
    } catch (error) {
        data = {};
    }

    let isOpen = false;

    const toggleButtonIcons = () => {
        const listIcon = '/ui/images/list-icon.svg';
        const closeIcon = '/ui/images/close-icon.svg';
        ['mobileMenuToggle', 'mobileMenuToggleMobile'].forEach((id) => {
            const btn = document.getElementById(id);
            if (!btn) return;
            const img = btn.querySelector('img');
            if (img) img.src = isOpen ? closeIcon : listIcon;
            btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            btn.setAttribute('aria-label', isOpen ? translate('header.close-menu') : translate('header.menu'));
        });
    };

    const render = () => {
        ReactDOM.render(
            <MobileMenu data={data} open={isOpen} onClose={close} />,
            el
        );
        toggleButtonIcons();
    };

    const toggle = () => {
        isOpen = !isOpen;
        render();
    };
    const close = () => {
        isOpen = false;
        render();
    };

    render();

    ['mobileMenuToggle', 'mobileMenuToggleMobile'].forEach((id) => {
        const btn = document.getElementById(id);
        if (btn && !btn.dataset.mobileMenuBound) {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                toggle();
            });
            btn.dataset.mobileMenuBound = '1';
        }
    });

    //Edge-case, close mobile navigation if opened and screen width increases >= 1100 px.
    if (!window.__litium.mobileMenuResizeBound) {
        window.addEventListener('resize', () => {
            if (isOpen && window.matchMedia('(min-width: 1100px)').matches) {
                close();
            }
        });
        window.__litium.mobileMenuResizeBound = true;
    }
};

const setupHeaderScroll = () => {
    const header = document.getElementById('header');
    if (!header) return;

    const update = () => {
        if (window.scrollY > 0) {
            header.classList.add('header--scrolled');
        } else {
            header.classList.remove('header--scrolled');
        }
    };

    update();
    if (!window.__litium.headerScrollBound) {
        window.addEventListener('scroll', update, { passive: true });
        window.__litium.headerScrollBound = true;
    }
};

const setupResponsiveSearchSlot = () => {
    const search = document.getElementById('quickSearch');
    const desktopSlot = document.getElementById('quickSearchSlot');
    const mobileSlot = document.getElementById('quickSearchSlotMobile');
    if (!search || !desktopSlot || !mobileSlot) return;

    const placeSearch = () => {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const targetSlot = isMobile ? mobileSlot : desktopSlot;
        if (search.parentElement !== targetSlot) {
            targetSlot.appendChild(search);
        }
        const activePlaceholder = targetSlot.querySelector('.quick-search-placeholder--mobile-slot');
        const inactiveSlot = isMobile ? desktopSlot : mobileSlot;
        const inactivePlaceholder = inactiveSlot.querySelector('.quick-search-placeholder--mobile-slot');
        if (activePlaceholder) activePlaceholder.style.display = 'none';
        if (inactivePlaceholder) inactivePlaceholder.style.display = '';
    };

    placeSearch();
    if (!window.__litium.searchSlotResizeBound) {
        window.addEventListener('resize', placeSearch);
        window.__litium.searchSlotResizeBound = true;
    }
};

const bootstrapComponents = () => {
    if (document.getElementById('miniCart')) {
        ReactDOM.render(
            <Provider store={store}>
                <MiniCartContainer/>
            </Provider>,
    document.getElementById('miniCart')
        );
    }
    if (document.getElementById('miniFavourite')) {
        ReactDOM.render(
            <Provider store={store}>
                <FavouriteIcon/>
            </Provider>,
    document.getElementById('miniFavourite')
        );
    }
    if (document.getElementById('quickSearch')) {
        ReactDOM.render(
            <Provider store={store}>
                <QuickSearchContainer/>
            </Provider>,
    document.getElementById('quickSearch')
            );
    }
    if (document.getElementById('navbar')) {
        const logoUrl = document.getElementById('navbar').dataset.logoUrl;
        let mobileMenuExtraLinks = document.getElementById('navbar').dataset.mobileMenuExtraLinks;
        try {
            mobileMenuExtraLinks = JSON.parse(mobileMenuExtraLinks);
        } catch (error) {
            mobileMenuExtraLinks = [];
        }
        ReactDOM.render(
            <Provider store={store}>
                <NavigationContainer mobileMenuExtraLinks={mobileMenuExtraLinks} logoUrl={logoUrl}/>
            </Provider>,
    document.getElementById('navbar')
            );
    }
    if (document.getElementById('mainNavigation')) {
        const el = document.getElementById('mainNavigation');
        let data = {};
        try {
            data = JSON.parse(el.dataset.navigation || '{}');
        } catch (error) {
            data = {};
        }
        ReactDOM.render(<MainNavigation data={data}/>, el);
    }
    setupMobileMenu();
    setupResponsiveSearchSlot();
    setupHeaderScroll();
    if (document.getElementById('facetedSearch')) {
        ReactDOM.render(
            <Provider store={store}>
                <FacetedSearchContainer/>
            </Provider>,
    document.getElementById('facetedSearch')
            );
    }
    if (document.getElementById('facetedSearchCompact')) {
        ReactDOM.render(
            <Provider store={store}>
                <FacetedSearchCompactContainer/>
            </Provider>,
    document.getElementById('facetedSearchCompact')
            );
    }
    if (document.getElementById('globalNotification')) {
        ReactDOM.render(
            <Provider store={store}>
                <NotificationContainer/>
            </Provider>,
    document.getElementById('globalNotification')
            );
    }

    if (document.getElementById('myPagePersons')) {
        const PersonList =  DynamicComponent({
            loader: () => import('./Containers/PersonList.container')
        });
        const myPagePersons = document.getElementById('myPagePersons');

        const issuperuser = myPagePersons.dataset.issuperuser;
        const userExplanation = myPagePersons.dataset.userexplanation;

        ReactDOM.render(
            <Provider store={store}>
                <PersonList userExplanation={userExplanation} superUser={issuperuser.toLowerCase()=='true'}/>
            </Provider>,
    document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Containers/AddressList.container')
        });
        ReactDOM.render(
            <Provider store={store}>
                <AddressList />
            </Provider>,
    document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Containers/Checkout.container')
        });
        ReactDOM.render(
            <Provider store={store}>
                <Checkout />
            </Provider>,
    document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Containers/LightboxImages.container')
        });
        const rootElement = document.getElementById('lightBoxImages');
        const images = Array.from(rootElement.querySelectorAll('a')).map(img => ({ src: img.dataset.src }));
        const thumbnails = Array.from(rootElement.querySelectorAll('a img')).map(img => ({ src: img.src }));
        ReactDOM.render(
            <Provider store={store}>
                <LightboxImages images={images} thumbnails={thumbnails}/>
            </Provider>,
    document.getElementById('lightBoxImages')
        );
    }

    if (document.querySelectorAll('.slider').length>0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider')
        });
        Array.from(document.querySelectorAll('.slider')).forEach((slider, index) => {
            const values = [...slider.querySelectorAll('.slider__block')].map(block=>{
                return ({
                    image : block.dataset.image,
                    url : block.dataset.url,
                    text : block.dataset.text,
                })
            });
            if (values.length > 0) {
                ReactDOM.render(
                    <Slider values={values}/>,
            slider
                );
        }
        });
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Containers/BuyButton.container')
        });
        Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
            const { label, articleNumber, name, price, variant, brand, category, quantityFieldId, href, cssClass } = button.dataset;
            ReactDOM.render(
                <Provider store={store}>
                    <BuyButton {...{ label, articleNumber, name, price, variant, brand, category, quantityFieldId, href, cssClass }}/>
                </Provider>,
                button
            );
        });
    }

    if (document.querySelectorAll('reorder-button').length > 0) {
        const ReorderButton = DynamicComponent({
            loader: () => import('./Containers/ReorderButton.container')
        });
        Array.from(document.querySelectorAll('reorder-button')).forEach((button) => {
            const { cssClass, orderId, title } = button.dataset;
            const label = button.innerText;
            ReactDOM.render(
                <Provider store={store}>
                    <ReorderButton {...{ label, title, cssClass, orderId }}/>
                </Provider>,
                button
            );
        });
    }
    
    if(document.getElementById('toast-container')){
        ReactDOM.render(
            <ToastContainer  position="bottom-right" pauseOnHover={false} hideProgressBar autoClose={false}/>,
            document.getElementById('toast-container')
        );
    };
    
    if (document.getElementsByClassName('quantity-purchase').length > 0) {
        const QuantityPurchase = DynamicComponent({
            loader: () => import('./Containers/QuantityPurchase.Container')
        });
        Array.from(document.getElementsByClassName('quantity-purchase')).forEach((quantityPackage) => {
            const { quantityfieldid, packagesize, allowsingleitem, articlenumber, productname, price, variant, brand, category, isKeg, isCategoryPage} = quantityPackage.dataset;
            ReactDOM.render(
                <Provider store={store}>
                    <QuantityPurchase
                        {...{
                            packageSize: packagesize,
                            allowSingleItem: allowsingleitem,
                            articleNumber: articlenumber,
                            productName: productname,
                            price: price,
                            variant: variant,
                            brand: brand,
                            category: category,
                            isKeg: isKeg,
                            isCategoryPage: isCategoryPage === "True",
                        }}
                    />
                </Provider>,
                quantityPackage
            );
        });
    }

    if (document.getElementById('wishlist')) {
        const WishList = DynamicComponent({
            loader: () => import('./Containers/Wishlist.container')
        });
        const { wishlist } = window.__litium.preloadState.wishlist;
        ReactDOM.render(
            <Provider store={store}>
                <WishList {...{}} />
            </Provider>,
    document.getElementById('wishlist')
        );
    }

    if (document.getElementsByClassName('favourite-button').length > 0) {
        const FavouriteButton = DynamicComponent({
            loader: () => import('./Containers/FavouriteButton.container')
        });
        Array.from(document.getElementsByClassName('favourite-button')).forEach((favouriteButton) => {
            const { articlenumber } = favouriteButton.dataset;
            ReactDOM.render(
                <Provider store={store}>
                    <FavouriteButton {...{ articleNumber : articlenumber }}/>
                </Provider>,
                favouriteButton
            );
        });
    }
    
    if (document.getElementById('navcontroller')) {
    
        ReactDOM.render(
            <Provider store={store}>
                <NavControllerContainer/>
            </Provider>,
    document.getElementById('navcontroller')
            );
    }
    const productSliderBlockElems = document.getElementsByClassName(
        'product-slider-block-container'
      );
      if (productSliderBlockElems) {
        const ProductSliderBlock = DynamicComponent({
          loader: () => import('./Components/ProductSliderBlock'),
        });
        for (let elem of productSliderBlockElems) {
            const { quantityfieldid, packagesize, allowsingleitem, articlenumber, productname, price, variant, brand, category, isKeg, isCategoryPage } = elem.dataset;
          ReactDOM.render(
            <Provider store={store}>
              <ProductSliderBlock productCardElements={Array.from(elem.children)} 
                        {...{packageSize: packagesize,
                            allowSingleItem: allowsingleitem,
                            articleNumber: articlenumber,
                            productName: productname,
                            price: price,
                            variant: variant,
                            brand: brand,
                            category: category,
                            isKeg: isKeg,
                            isCategoryPage: isCategoryPage === "True",
                        }}   
                        />
            </Provider>,
            elem
          );
        }
      }

    const brandList = document.getElementById('brandList');
    if (brandList) {
        const BrandListContainer = DynamicComponent({
            loader: () => import("./Containers/BrandListContainer")
        });
        let brandsJson = JSON.parse(brandList.dataset["json"]);
        ReactDOM.render(
            <Provider store={store}>
                <BrandListContainer title={brandList.dataset["title"]} brands={brandsJson}/>
            </Provider>,
            brandList
        );
    }

    const productExport = document.getElementsByClassName("product-export__btn");
    if (productExport){
        const ProductExportContainer = DynamicComponent({
            loader: () => import("./Containers/ProductExportButton.container")
        });
        Array.from(productExport).forEach((productExportBtn) => {
            let buttonText = productExportBtn.dataset.buttonText;
            let fetchingText = productExportBtn.dataset.fetchingText;
            ReactDOM.render(
                <Provider store={store}>
                    <ProductExportContainer buttonText={buttonText} fetchingText={fetchingText} />
                </Provider>,
                productExportBtn
            );
        });
    }

    const orderHistory = document.getElementById("orderHistory");
    if(orderHistory){
        const OrderHistory = DynamicComponent({
            loader: () => import("./Containers/OrderHistory.container")
        });

        ReactDOM.render(
            <Provider store={store}><OrderHistory /></Provider>,
            orderHistory
        )
    }
}

bootstrapComponents();